/**
 * Provides a gui for uploading images in bulk.
 * @module images/upload-bulk
 */
import 'muicss-r18/lib/sass/mui.scss'
import React, { Component } from 'react'
import { connect as connectRedux } from 'react-redux'
import { Container, CssBaseline, ThemeProvider, createTheme } from '@mui/material'
import { compose } from 'recompose'
import { List } from 'immutable'
import Appbar from '../app-bar'
import LoginForm from '../login-form'
import Modal from '../modal'
import Toast from '../common/Toast'
import './index.scss'

// Create a basic theme that matches your existing design
const theme = createTheme({
  palette: {
    primary: {
      main: '#2471A3', // Your existing theme-primary color
    },
    secondary: {
      main: '#00BFFF', // deepskyblue converted to hex
    },
    background: {
      default: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica Neue", "Helvetica", "Arial", sans-serif',
    fontSize: 14,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
  },
  components: {
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#00BFFF', // theme-secondary color
          textDecoration: 'none',
          transition: 'color 0.3s ease',
          '&:hover, &:active, &:focus': {
            color: '#0099CC', // darken(theme-secondary, 20%)
            textDecoration: 'none',
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {},
        cell: {},
        columnHeader: {},
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {},
        a: {
          color: '#00BFFF', // theme-secondary color
          textDecoration: 'none',
          transition: 'color 0.3s ease',
          '&:hover, &:active, &:focus': {
            color: '#0099CC', // darken(theme-secondary, 20%)
            textDecoration: 'none',
          },
        },
      },
    },
  },
})

const ErrorTemplate = ({ showMenu, username, error }) => (
  <>
    <Appbar showMenu={showMenu} username={username} />
    <Container className="page__container" maxWidth={false}>
      <main className="page__title">
        <div>{error}</div>
      </main>
    </Container>
  </>
)

class Page extends Component {
  renderChildren = () => {
    const { props } = this
    const {
      userAbuseUnresolvedCount,
      allowedRolesKey,
      allowedPermissions = List(),
      showMenu = true,
      modalOpen,
      className,
      children,
      config,
      auth,
    } = props
    const jwt = auth.getIn(['data', 'jwt'])
    const username = auth.getIn(['data', 'username'])
    const pageEntitlements = auth.getIn(['data', 'pageEntitlements'])
    const permissions = auth.getIn(['data', 'permissions'], List())

    if (!config) {
      return <div>Loading config...</div>
    }

    if (!jwt) {
      return (
        <Container className="page__container">
          <Appbar showMenu={false} />
          <LoginForm className="page__login-form" />
        </Container>
      )
    }

    if (allowedRolesKey && !pageEntitlements.includes(allowedRolesKey)) {
      return (
        <ErrorTemplate
          showMenu={showMenu}
          username={username}
          error="You are not entitled to view this page, proceed to your nearest exit"
        />
      )
    }

    if (!allowedPermissions.isEmpty() && !allowedPermissions.some((v) => permissions.includes(v))) {
      return (
        <ErrorTemplate
          showMenu={showMenu}
          username={username}
          error="You do not have the permissions needed to view this page"
        />
      )
    }

    return (
      <React.Fragment>
        {userAbuseUnresolvedCount > 0 ? (
          <a href={'/user-abuse'} className="page__report-count">
            <span className="page__red">{`${userAbuseUnresolvedCount} `}</span>
            Unresolved Reports
          </a>
        ) : null}
        <Appbar showMenu={showMenu} username={username} />
        <Container className="page__container" maxWidth={false}>
          <main className={className}>{children}</main>
        </Container>
        {modalOpen && <Modal />}
        <Toast />
      </React.Fragment>
    )
  }

  render () {
    return (
      <>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <article className="page">{this.renderChildren()}</article>
        </ThemeProvider>
      </>
    )
  }
}

export default compose(
  connectRedux((state) => ({
    userAbuseUnresolvedCount: state.userAbuse.get('unresolvedCount', 0),
    modalOpen: state.modal.get('isOpen'),
    config: state.config,
    auth: state.auth,
  })),
)(Page)
